import { DOCUMENT } from '@angular/common';
import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';

import { AuthSessionService } from '../services/auth-session.service';
import { UserContextService } from '../services/user-context.service';
import { LocaleService } from '../../services/locale.service';
import { environment } from '../../../../environments/environment';

/**
 * Guard that redirects users to the correct landing page based on their role after login.
 * - Admins go to /user-management
 * - Redirect roles go to the external SHIP application
 * - All other roles go to /shipments
 * - If not authenticated, triggers login
 */
export const roleRedirectGuard: CanActivateFn = async () => {
  const authSessionService = inject(AuthSessionService);
  const userContextService = inject(UserContextService);
  const router = inject(Router);
  const localeService = inject(LocaleService);
  const document = inject(DOCUMENT);

  await authSessionService.initialize();

  if (authSessionService.hasExternalRedirectStarted()) {
    return false;
  }

  if (!authSessionService.isAuthEnabled()) {
    return router.parseUrl(toRouterPath(localeService, '/reports'));
  }

  if (!authSessionService.isAuthenticated()) {
    await authSessionService.login();
    return false;
  }

  // Legacy behavior: landing decision is based on backend user role.
  const backendContext = await userContextService.loadUserAndPermissions();
  if (!backendContext?.user) {
    return router.parseUrl(toRouterPath(localeService, '/unauthorized'));
  }

  const primaryRole = backendContext.user.role?.toLowerCase();

  if (primaryRole === 'admin') {
    return router.parseUrl(toRouterPath(localeService, '/user-management'));
  }

  if (primaryRole?.includes('redirect')) {
    document.defaultView?.location.assign(environment.externalLinks.shipSso);
    return false;
  }

  return router.parseUrl(toRouterPath(localeService, '/shipments'));
};

function toRouterPath(localeService: LocaleService, path: string): string {
  return localeService.stripSupportedLocalePrefix(localeService.withLocalePath(path));
}
